import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class XlsxUtilsService  {
  private SSF: any = {}; // SSF is kept as any since its type is unknown

  constructor() {}

  /**
   * Decodes an Excel range (e.g., "A1:D10") into a structured object
   */
  safeDecodeRange(range: string): { s: { c: number; r: number }; e: { c: number; r: number } } {
    const o = { s: { c: 0, r: 0 }, e: { c: 0, r: 0 } };
    let idx = 0, i = 0, cc = 0, len = range.length;

    // Decode start column
    for (idx = 0; i < len; ++i) {
      cc = range.charCodeAt(i) - 64; // Convert A-Z to numerical index
      if (cc < 1 || cc > 26) break;
      idx = 26 * idx + cc;
    }
    o.s.c = --idx;

    // Decode start row
    for (idx = 0; i < len; ++i) {
      cc = range.charCodeAt(i) - 48; // Convert '0'-'9' to numerical index
      if (cc < 0 || cc > 9) break;
      idx = 10 * idx + cc;
    }
    o.s.r = --idx;

    // If there's no second range, return early
    if (i === len || range.charCodeAt(++i) === 58) {
      o.e.c = o.s.c;
      o.e.r = o.s.r;
      return o;
    }

    // Decode end column
    for (idx = 0; i !== len; ++i) {
      cc = range.charCodeAt(i) - 64;
      if (cc < 1 || cc > 26) break;
      idx = 26 * idx + cc;
    }
    o.e.c = --idx;

    // Decode end row
    for (idx = 0; i !== len; ++i) {
      cc = range.charCodeAt(i) - 48;
      if (cc < 0 || cc > 9) break;
      idx = 10 * idx + cc;
    }
    o.e.r = --idx;
    return o;
  }

  /**
   * Encodes a column index into an Excel column name (e.g., 0 -> "A", 27 -> "AA")
   */
  encodeCol(col: number): string {
    let s = '';
    for (++col; col; col = Math.floor((col - 1) / 26)) {
      s = String.fromCharCode(((col - 1) % 26) + 65) + s;
    }
    return s;
  }

  /**
   * Encodes a row index into an Excel row number (e.g., 0 -> "1")
   */
  encodeRow(row: number): string {
    return (row + 1).toString();
  }

  /**
   * Formats a cell value using the provided format settings
   */
  safeFormatCell(cell: any, value?: any): string {
    try {
      if (cell.z !== undefined) {
        return (cell.w = this.SSF.format(cell.z, value));
      }
      if (!cell.XF) return value;
      return (cell.w = this.SSF.format(cell.XF.ifmt || 0, value));
    } catch {
      return value ? value.toString() : '';
    }
  }

  /**
   * Ensures a cell is formatted correctly
   */
  formatCell(cell: any, value?: any): string {
    if (!cell || cell.t == null) return '';
    if (cell.w !== undefined) return cell.w;
    return this.safeFormatCell(cell, value ?? cell.v);
  }

  /**
   * Converts an Excel sheet to CSV format
   */
  sheetToCsv(sheet: any, opts?: { FS?: string; RS?: string }): string {
    if (!sheet || !sheet['!ref']) return '';

    const out: string[] = [];
    const o = opts ?? {};
    const { s, e } = this.safeDecodeRange(sheet['!ref']);
    const FS = o.FS ?? ',';
    const RS = o.RS ?? '\n';
    const qreg = /"/g;

    const cols: string[] = [];
    for (let C = s.c; C <= e.c; ++C) {
      cols[C] = this.encodeCol(C);
    }

    for (let R = s.r; R <= e.r; ++R) {
      const row: string[] = [];
      const rr = this.encodeRow(R);

      for (let C = s.c; C <= e.c; ++C) {
        let txt = '';
        const val = sheet[cols[C] + rr];

        if (val !== undefined) {
          txt = this.formatCell(val, '');
        }

        if (txt.includes(FS) || txt.includes(RS) || txt.includes('"')) {
          txt = `"${txt.replace(qreg, '""')}"`; // Escape quotes
        }

        row.push(txt);
      }

      out.push(row.join(FS));
    }

    return out.join(RS);
  }
}
